import {
 createSlice,
 createAsyncThunk,
 ThunkDispatch,
 AnyAction,
} from "@reduxjs/toolkit";
import { LocalStorage } from "../../types/local_storage.enum";
import { AuthState, logout } from "./authSlice";
import { AppDispatch } from "../store"; // Adjust path as needed
import { BACKEND_URL } from "../../utils/env.config";
import {
 GetInterviewsResponse,
 JobInterview,
} from "../../types/interview.v2.type";
import { InterviewScore } from "../../types/interview.type";
import { DetectOSAndBrowser } from "../api/DetectBroweser";

interface InterviewListState {
 loading: boolean;
 error: string | null;
 totalCount: number;
 interviews: JobInterview[];
 interviewScores: InterviewScore[];
}

// ✅ Fix: Pass dispatch instead of using useDispatch
const refreshToken = async (
 dispatch: ThunkDispatch<
  { interviewList: InterviewListState; auth: AuthState; interviewApi: any },
  unknown,
  AnyAction
 >
): Promise<string | null> => {
 const refreshToken = localStorage.getItem(LocalStorage.REFRESH_TOKEN);
 const token = localStorage.getItem(LocalStorage.TOKEN);

 if (!refreshToken || !token) return null;

 const response = await fetch(`${BACKEND_URL}Auth/RefreshToken`, {
  method: "POST",
  headers: {
   "Content-Type": "application/json",
   "Client-OS": DetectOSAndBrowser().OS,
   "Client-Browser": DetectOSAndBrowser().Browser,
  },
  body: JSON.stringify({
   RefreshToken: refreshToken,
   Token: token,
  }),
 });

 if (!response.ok) {
  dispatch(logout());
  return null;
 }

 const data = await response.json();
 const newToken = data.token;

 if (newToken) {
  sessionStorage.setItem("token", newToken);
  return newToken;
 }

 return null;
};

// ✅ Fix: Pass dispatch into the fetch helper
const fetchWithAuthRetry = async (
 url: string,
 options: RequestInit,
 dispatch: AppDispatch
): Promise<Response> => {
 let response = await fetch(url, options);

 if (response.status === 401) {
  const newToken = await refreshToken(dispatch);

  if (newToken) {
   response = await fetch(url, {
    ...options,
    headers: {
     ...options.headers,
     Authorization: `Bearer ${newToken}`,
    },
   });
  }
 }

 return response;
};

// ✅ Fetch Interviews
export const fetchInterviews = createAsyncThunk<
 GetInterviewsResponse,
 void,
 {
  state: {
   interviewList: InterviewListState;
   auth: AuthState;
   interviewApi: any;
  };
  dispatch: AppDispatch;
  rejectValue: string;
 }
>("interviewList/fetchInterviews", async (_, { rejectWithValue, dispatch }) => {
 try {
  const token = localStorage.getItem(LocalStorage.TOKEN);
  const response = await fetchWithAuthRetry(
   `${BACKEND_URL}Interview/GetInterviews?pageIndex=0`,
   {
    method: "GET",
    headers: {
     Authorization: `Bearer ${token}`,
     "Client-OS": DetectOSAndBrowser().OS,
     "Client-Browser": DetectOSAndBrowser().Browser,
    },
   },
   dispatch
  );

  if (!response.ok) {
   throw new Error("Failed to fetch interviews");
  }

  const data: GetInterviewsResponse = await response.json();
  return data;
 } catch (error: any) {
  return rejectWithValue(error.message);
 }
});

// ✅ Fetch Interview Scores
export const fetchInterviewScores = createAsyncThunk<
 InterviewScore[],
 number,
 {
  state: {
   interviewList: InterviewListState;
   auth: AuthState;
   interviewApi: any;
  };
  dispatch: AppDispatch;
  rejectValue: string;
  rejectWithValue: (value: string) => string;
 }
>(
 "interviewList/fetchInterviewScores",
 async (interviewId: number, { rejectWithValue, dispatch }) => {
  try {
   const token = localStorage.getItem(LocalStorage.TOKEN);
   const response = await fetchWithAuthRetry(
    `${BACKEND_URL}Interview/GetInterviewScore?interviewId=${interviewId}`,
    {
     method: "GET",
     headers: {
      Authorization: `Bearer ${token}`,
      "Client-OS": DetectOSAndBrowser().OS,
      "Client-Browser": DetectOSAndBrowser().Browser,
     },
    },
    dispatch
   );

   if (!response.ok) {
    return rejectWithValue("Failed to fetch interview scores");
   }

   const data: InterviewScore[] = await response.json();
   return data;
  } catch (error: any) {
   return rejectWithValue(error.message || "An error occurred");
  }
 }
);

// Slice state
const initialState: InterviewListState = {
 loading: false,
 error: null,
 totalCount: 0,
 interviews: [],
 interviewScores: [],
};

// Interview slice
export const interviewListSlice = createSlice({
 name: "interviewList",
 initialState,
 reducers: {},
 extraReducers: (builder) => {
  builder
   .addCase(fetchInterviews.pending, (state) => {
    state.loading = true;
    state.error = null;
   })
   .addCase(fetchInterviews.fulfilled, (state, action) => {
    state.loading = false;
    state.error = null;
    state.totalCount = action.payload.totalCount;
    state.interviews = action.payload.jobInterviews;
   })
   .addCase(fetchInterviews.rejected, (state, action) => {
    state.loading = false;
    state.error = action.payload as string;
   })

   .addCase(fetchInterviewScores.pending, (state) => {
    state.loading = true;
    state.error = null;
    state.interviewScores = [];
   })
   .addCase(fetchInterviewScores.fulfilled, (state, action) => {
    state.loading = false;
    state.error = null;
    state.interviewScores = action.payload;
   })
   .addCase(fetchInterviewScores.rejected, (state, action) => {
    state.loading = false;
    state.error = action.payload as string;
    state.interviewScores = [];
   });
 },
});

export default interviewListSlice.reducer;
